import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/authContext.tsx";

const STORAGE_PREFIX = "webadmincc-recents_";
const EMPTY: string[] = [];
const MAX = 5;
const EVT = "webadmincc-recents-changed";

function keyFor(uid: number | undefined, endpointId: number | undefined): string | null {
  if (uid == null || !endpointId) return null;
  return `${STORAGE_PREFIX}${uid}_${endpointId}`;
}

function read(key: string | null): string[] {
  if (!key) return EMPTY;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : EMPTY;
  } catch {
    return EMPTY;
  }
}

function write(key: string, list: string[]): void {
  localStorage.setItem(key, JSON.stringify(list));
  window.dispatchEvent(new Event(EVT));
}

/**
 * Tracks the last `MAX` pages the user visited, newest first. Lives in
 * localStorage per user-id so it's stable across tabs / sessions.
 *
 * `recordRecent(name)` is idempotent — calling with the already-top page is
 * a no-op. Call from sideNav on page change; no-op is fine on Dashboard home.
 */
export function useRecents() {
  const { session } = useAuth();
  const key = keyFor(session?.UID, session?.ActiveEndPointID);
  const [recents, setRecents] = useState<string[]>(() => read(key));

  useEffect(() => {
    setRecents(read(key));
    function onUpdate() { setRecents(read(key)); }
    window.addEventListener(EVT, onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener(EVT, onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, [key]);

  const recordRecent = useCallback(
    (pageName: string) => {
      if (!key) return;
      const lower = pageName.toLowerCase();
      const current = read(key);
      if (current[0] === lower) return;
      const next = [lower, ...current.filter((n) => n !== lower)].slice(0, MAX);
      write(key, next);
    },
    [key],
  );

  const clearRecents = useCallback(() => {
    if (!key) return;
    write(key, []);
  }, [key]);

  return { recents, recordRecent, clearRecents };
}
